/** Video / Asset compact node — media-first card mirroring `ImageAssetNode`
 * (VISTOERA_CANVAS_VIDEO_ASSET_NODE_AND_IMAGE_TO_VIDEO_QUICK_ACTION_V1).
 *
 * "视频 / 素材" — a video that entered THIS project via upload/pick from
 * the canonical video asset library. Preview uses shared `CanvasVideoPreview`
 * (central Play overlay, first-frame + ended reset) and inline detail below
 * the media body (same pattern as Generate Video). */

import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  LoadingOutlined,
  ReloadOutlined,
  SwapOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Button, Tooltip } from 'antd'
import { memo, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import CanvasResultQuickActionRail from './CanvasResultQuickActionRail'
import CanvasVideoPreview, { type CanvasVideoPreviewHandle } from '../CanvasVideoPreview'
import { functionCardShowsInspector, isStickyMediaPlayback } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { useNodeDefinition } from '../nodeRegistry'
import { computeMediaBoxSize } from '../mediaBoxSize'
import MediaLightbox from '../MediaLightbox'
import { getPlaybackTime } from '../videoPlaybackCoordinator'
import GenericDetailRenderer from '../nodeDetail/GenericDetailRenderer'
import { useSingleOrDoubleClick } from '../useSingleOrDoubleClick'
import { downloadCanvasMedia } from '../canvasDownload'
import {
  resolveBoundVideoPlaybackUrl,
  resolveBoundVideoPosterUrl,
  type VideoAssetBindSnapshot,
} from '../canvasVideoAssetResolve'
import type { CanvasNodeDto, FlowNodeData } from '../types'

const HEADER_HEIGHT = 34

function readVideoAssetSnapshot(fields: Record<string, unknown> | undefined): VideoAssetBindSnapshot | null {
  const raw = fields?.videoAssetSnapshot
  if (!raw || typeof raw !== 'object') return null
  return raw as VideoAssetBindSnapshot
}

function VideoAssetNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const label = (nodeData.fields?.label as string | undefined) || nodeLabel(def.nodeType, def.label)
  const entityId = nodeData.entityId
  const workspaceId = nodeData.workspaceId
  const snapshot = readVideoAssetSnapshot(nodeData.fields)
  // VistoeraTASK-00220 — client-only upload lifecycle for a node created
  // from a local OS file drop, before its canonical asset exists (same
  // `localUpload` contract `ImageAssetNode.tsx` already ships).
  const localUpload = nodeData.localUpload || null
  // A still-local (never persisted) upload placeholder counts as
  // "content" for width/click-gating purposes even though `entityId`
  // is still null.
  const hasContent = Boolean(entityId) || Boolean(localUpload)
  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showInspector = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)
  const stickyPlayback = isStickyMediaPlayback(isDetailExpanded, nodeData.isDragTempCollapsed)
  const videoPreviewRef = useRef<CanvasVideoPreviewHandle>(null)

  const [previewOpen, setPreviewOpen] = useState(false)
  const [modalStartTime, setModalStartTime] = useState(0)
  const [box, setBox] = useState(() => computeMediaBoxSize(0, 0))
  const updateNodeInternals = useUpdateNodeInternals()

  const playbackUrl = useMemo(() => {
    if (!entityId || !workspaceId) return null
    return resolveBoundVideoPlaybackUrl(workspaceId, entityId, snapshot)
  }, [entityId, workspaceId, snapshot])

  const posterUrl = useMemo(() => {
    if (!entityId || !workspaceId) return undefined
    return resolveBoundVideoPosterUrl(workspaceId, entityId, snapshot)
  }, [entityId, workspaceId, snapshot])

  const mediaAreaHeight = entityId ? box.height : undefined
  const handleTop = entityId ? (HEADER_HEIGHT + box.height) / 2 : undefined

  const nodeDto = useMemo<CanvasNodeDto>(
    () => ({
      id,
      node_type: nodeData.nodeType,
      category: def.category,
      entity_type: entityId ? 'video_asset' : null,
      entity_id: entityId ?? null,
      parent_node_id: null,
      position: { x: 0, y: 0 },
      width: null,
      height: null,
      collapsed: false,
      z_index: 0,
      data: nodeData.fields ?? {},
      ui_state: {},
      latest_run_id: nodeData.latestRunId ?? null,
    }),
    [id, nodeData.nodeType, nodeData.fields, nodeData.latestRunId, def.category, entityId],
  )

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, box.width, box.height, isDetailExpanded, updateNodeInternals])

  const openPicker = () => {
    if (!workspaceId || !nodeData.onOpenVideoAssetPicker) return
    nodeData.onOpenVideoAssetPicker({
      nodeId: id,
      title: entityId ? t('mediaCard.replaceVideo') : t('mediaCard.selectVideo'),
      initialSelection: entityId,
    })
  }

  const handleEmptyClick = () => {
    openPicker()
  }

  const { onClick: onMediaSingleClick, onDoubleClick: onMediaDoubleClick } = useSingleOrDoubleClick(
    () => {
      nodeData.onExpandFunctionCardDetail?.(id)
      videoPreviewRef.current?.activateStickyPlayback()
    },
    () => {
      if (!playbackUrl) return
      setModalStartTime(getPlaybackTime(playbackUrl))
      setPreviewOpen(true)
    },
  )

  const handleSwapClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    openPicker()
  }

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }

  const handleDownloadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!playbackUrl) return
    downloadCanvasMedia(`${playbackUrl}${playbackUrl.includes('?') ? '&' : '?'}download=true`)
  }

  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    // A still-local (never persisted) upload placeholder has no
    // Canvas-node-delete-confirm / asset-lifecycle concerns yet — same
    // rationale as `ImageAssetNode`'s own `handleDeleteClick`.
    if (localUpload) {
      nodeData.onRemoveLocalVideoUpload?.(id)
      return
    }
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }

  const handleRetryLocalUploadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onRetryLocalVideoUpload?.(id)
  }

  const handleRemoveLocalUploadClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onRemoveLocalVideoUpload?.(id)
  }

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--video-media${selected ? ' is-selected' : ''}${!hasContent ? ' is-empty' : ''} ${canvasNodeShellStateClass(showInspector)}`}
      style={{ width: entityId ? box.width : undefined }}
      onClick={hasContent ? undefined : handleEmptyClick}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="pcv-handle pcv-handle--lg"
        style={handleTop != null ? { top: handleTop } : undefined}
      />
      <div className="pcv-node-inner">
        <div className="pcv-media-header">
          <span className="pcv-media-icon" style={{ color: def.color, background: `${def.color}22` }}>
            {def.icon}
          </span>
          <span className="pcv-media-title" title={label}>
            {label}
          </span>
          <div className="pcv-media-hover-toolbar nodrag">
            {entityId ? (
              <Tooltip title={t('common.download')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('common.download')} onClick={handleDownloadClick}>
                  <DownloadOutlined />
                </button>
              </Tooltip>
            ) : null}
            {entityId ? (
              <Tooltip title={t('common.copy')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('common.copy')} onClick={handleCopyClick}>
                  <CopyOutlined />
                </button>
              </Tooltip>
            ) : null}
            {entityId ? (
              <Tooltip title={t('mediaCard.replaceVideo')} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={t('mediaCard.replaceVideo')} onClick={handleSwapClick}>
                  <SwapOutlined />
                </button>
              </Tooltip>
            ) : null}
            <Tooltip title={t('common.delete')} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" aria-label={t('common.delete')} onClick={handleDeleteClick}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </div>
        </div>
        <div
          className={`pcv-media-body ${NODE_EXPAND_TRIGGER_CLASS}`}
          style={{ height: mediaAreaHeight }}
          onClick={
            entityId
              ? (e) => {
                  e.stopPropagation()
                  onMediaSingleClick()
                }
              : undefined
          }
          onDoubleClick={
            entityId
              ? (e) => {
                  e.stopPropagation()
                  onMediaDoubleClick()
                }
              : undefined
          }
        >
          {playbackUrl ? (
            <CanvasVideoPreview
              ref={videoPreviewRef}
              key={playbackUrl}
              assetId={playbackUrl}
              src={playbackUrl}
              poster={posterUrl}
              className="pcv-media-image"
              onLoadedDimensions={(w, h) => setBox(computeMediaBoxSize(w, h))}
              stickyPlayback={stickyPlayback}
              isNodeDragging={nodeData.isNodeDragging}
              playbackResetKey={nodeData.playbackResetKey}
            />
          ) : localUpload?.status === 'uploading' ? (
            <div className="pcv-media-local-upload-overlay" data-testid="video-asset-uploading">
              <LoadingOutlined spin />
              <span className="pcv-media-local-upload-filename">{localUpload.filename}</span>
              <span className="pcv-media-local-upload-label">{t('mediaCard.uploadingVideo')}</span>
            </div>
          ) : localUpload?.status === 'reconciling' ? (
            /* VistoeraTASK-00232 - same non-terminal reconciling overlay
             * ImageAssetNode ships (spec section 16, "Ambiguous Timeout
             * != FAILED") - no Retry/Remove here, resolves automatically
             * within one round-trip to either success (overlay
             * disappears) or 'error' below. */
            <div className="pcv-media-local-upload-overlay" data-testid="video-asset-reconciling">
              <LoadingOutlined spin />
              <span className="pcv-media-local-upload-filename">{localUpload.filename}</span>
              <span className="pcv-media-local-upload-label">{t('mediaCard.confirmingUploadStatus')}</span>
            </div>
          ) : localUpload?.status === 'error' ? (
            <div className="pcv-media-local-upload-overlay pcv-media-local-upload-overlay--error nodrag" data-testid="video-asset-upload-failed">
              <span className="pcv-media-local-upload-label">{t('mediaCard.uploadVideoFailed')}</span>
              <div className="pcv-media-local-upload-actions">
                <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryLocalUploadClick} data-testid="video-asset-retry-upload">
                  {t('common.retry')}
                </Button>
                <Button size="small" danger icon={<CloseOutlined />} onClick={handleRemoveLocalUploadClick} data-testid="video-asset-remove-upload">
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="pcv-media-placeholder">
              <VideoCameraOutlined />
              {!hasContent ? <span className="pcv-media-placeholder-hint">{t('mediaCard.clickToSelectVideo')}</span> : null}
            </div>
          )}
        </div>
      </div>

      {showInspector && entityId && nodeData.onChangeFields ? (
        <div className="pcv-gen-inspector nodrag" data-testid="video-asset-inline-detail">
          <GenericDetailRenderer node={nodeDto} workspaceId={workspaceId || ''} onChangeFields={nodeData.onChangeFields} />
        </div>
      ) : null}

      <Handle
        type="source"
        position={Position.Right}
        id="video-out"
        className="pcv-handle pcv-handle--lg"
        style={handleTop != null ? { top: handleTop } : undefined}
      />

      {/* TASK-00584 — Admin-driven external Quick Action (video_asset ->
       * motion_reconstruction). Backs the pre-existing
       * `onQuickCreateMotionReconstruction` Canvas handler (it only
       * creates + connects the node; never auto-submits a job). */}
      {entityId && nodeData.onQuickCreateMotionReconstruction ? (
        <CanvasResultQuickActionRail
          sourceNodeType="video_asset"
          plainIcons
          actionHandlers={[
            {
              targetNodeType: 'motion_reconstruction',
              tooltip: t('motionAsset.quickAction.reconstruct'),
              onClick: () => nodeData.onQuickCreateMotionReconstruction?.(id),
            },
          ]}
        />
      ) : null}

      <MediaLightbox
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false)
          setModalStartTime(0)
        }}
        kind="video"
        src={playbackUrl}
        assetId={playbackUrl}
        startTime={modalStartTime}
      />
    </div>
  )
}

export default memo(VideoAssetNodeImpl)
